// ===============================
// algo-gardes-pr2.js
// ===============================
// Algorithme dédié à la génération des gardes PR2 (Port Royal 2).
//
// Approche : workflow interactif en phases successives (comme le thoracique).
//
// PHASE 1 : « 1 garde pour chacun »
//           Objectif : attribuer exactement 1 garde à chaque médecin éligible.
//           Stratégie : médecins les plus contraints d'abord (moins de jours dispo).
//           Priorité : code 1 (souhaite absolument) > code 2 (disponible).
//           Espacement : on choisit le jour le plus « central » si plusieurs candidats.
//
// PHASE 2 : « 2e garde pour chacun »
//           Objectif : attribuer une 2e garde à chaque médecin déjà servi.
//           Priorité : code 1 > code 2, jour le plus éloigné de la 1re garde.
//           Contrainte : espacement minimum de 6 jours entre deux gardes PR2 du même médecin.
//
// Entrée  : { desiderata, year, monthNum }
// Sortie  : résultat de chaque phase séparément
// ===============================

// ═══════════════════════════════════════
// UTILITAIRES
// ═══════════════════════════════════════

function pad2(n) {
  return String(n).padStart(2, "0");
}

/**
 * Jour de la semaine : 0=dimanche … 6=samedi (calendrier ISO, UTC — évite les décalages fuseau serveur).
 */
function dayOfWeek(dateStr) {
  return parseIsoDate(dateStr).getUTCDay();
}

/**
 * Nom court du jour (3 lettres).
 */
export function jourNomCourt(dateStr) {
  return ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"][dayOfWeek(dateStr)];
}

/**
 * Vérifie si une date est un vendredi, samedi ou dimanche.
 */
export function isWeekend(dateStr) {
  const d = dayOfWeek(dateStr);
  return d === 5 || d === 6 || d === 0;
}

function parseIsoDate(dateStr) {
  return new Date(`${dateStr}T00:00:00Z`);
}

function toIsoDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDaysIso(dateStr, delta) {
  const d = parseIsoDate(dateStr);
  d.setUTCDate(d.getUTCDate() + delta);
  return toIsoDate(d);
}

function diffDaysIso(a, b) {
  const ms = parseIsoDate(a).getTime() - parseIsoDate(b).getTime();
  return Math.round(ms / 86400000);
}

function computeEasterSundayIso(year) {
  // Algorithme de Meeus/Jones/Butcher (grégorien)
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3=mars, 4=avril
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function frenchPublicHolidays(year) {
  const easter = computeEasterSundayIso(year);
  const easterMonday = addDaysIso(easter, 1);
  const ascension = addDaysIso(easter, 39);
  const pentecostMonday = addDaysIso(easter, 50);
  return new Set([
    `${year}-01-01`, // Jour de l'an
    easterMonday, // Lundi de Pâques
    `${year}-05-01`, // Fête du Travail
    `${year}-05-08`, // Victoire 1945
    ascension, // Ascension
    pentecostMonday, // Lundi de Pentecôte
    `${year}-07-14`, // Fête Nationale
    `${year}-08-15`, // Assomption
    `${year}-11-01`, // Toussaint
    `${year}-11-11`, // Armistice
    `${year}-12-25`, // Noël
  ]);
}

function isJourFerie(dateStr) {
  const year = parseIsoDate(dateStr).getFullYear();
  return frenchPublicHolidays(year).has(dateStr);
}

// ── Répartition PR2 : au plus un jour « chargé » (sam / dim / férié) par médecin et par mois ──

/** Samedi, dimanche ou jour férié FR (compte comme une seule charge calendrier). */
function pr2JourEstWeekendOuFerie(dateStr) {
  if (isJourFerie(dateStr)) return true;
  const dow = dayOfWeek(dateStr);
  return dow === 0 || dow === 6;
}

/** Nombre de PR2 déjà en sam / dim / férié pour ce médecin (chaque date compte au plus une fois). */
function pr2NbPr2WeekendOuFerie(med, affectationsMap) {
  let n = 0;
  for (const [d, m] of Object.entries(affectationsMap || {})) {
    if (m !== med) continue;
    if (pr2JourEstWeekendOuFerie(d)) n++;
  }
  return n;
}

/**
 * true si une nouvelle PR2 le `dateStr` est autorisée : au plus une PR2 en samedi, dimanche ou jour férié
 * pour ce médecin sur les affectations courantes (toutes phases confondues).
 */
function pr2CalendrierAutoriseNouveauJour(dateStr, med, affectationsMap) {
  if (!pr2JourEstWeekendOuFerie(dateStr)) return true;
  return pr2NbPr2WeekendOuFerie(med, affectationsMap) === 0;
}

/** Liste de dates (même médecin) : au plus une est sam / dim / férié. */
function pr2CalendrierEnsembleValidePourDates(dates) {
  let n = 0;
  for (const dateStr of dates) {
    if (pr2JourEstWeekendOuFerie(dateStr)) n++;
  }
  return n <= 1;
}

/** Lun–ven hors férié (favoriser pour équilibrer semaine / week-end). */
function pr2JourEstSemaineOuvree(dateStr) {
  const dow = dayOfWeek(dateStr);
  if (dow === 0 || dow === 6) return false;
  if (isJourFerie(dateStr)) return false;
  return true;
}

function pr2MedecinAChargeWeekendOuFerie(med, affectationsMap) {
  return pr2NbPr2WeekendOuFerie(med, affectationsMap) > 0;
}

function coeffJour(dateStr) {
  if (isJourFerie(dateStr)) return 2;
  const d = dayOfWeek(dateStr); // 0=dim, 6=sam
  if (d === 0) return 2;
  if (d === 6) return 1.5;
  return 1;
}

function chargeGardeByDate(dateStr) {
  return POIDS_GARDE * coeffJour(dateStr);
}

function chargeAstreinteByDate(dateStr) {
  return POIDS_ASTREINTE * coeffJour(dateStr);
}

/**
 * Label lisible d'un code de préférence.
 */
export function prefLabel(code) {
  switch (code) {
    case 1: return "Souhaite absolument";
    case 2: return "Disponible";
    case 3: return "Peu disponible";
    case 0: return "Indisponible";
    default: return "—";
  }
}

// ═══════════════════════════════════════
// PRÉPARATION DES DONNÉES
// ═══════════════════════════════════════

/**
 * Prépare les structures internes à partir des désidérata bruts.
 *
 * @param {Array} desiderata - Tableau d'objets { nom, prenom, nbGardes, valeurs: { date: code|{code} } }
 * @param {number} year      - Année (ex: 2026)
 * @param {number} monthNum  - Mois 1-12
 * @returns {{ souhaits, jours, medics, getPref, eligibles }}
 */
export function preparerDonnees(desiderata, year, monthNum, { zeroGardesExcludes = false } = {}) {
  const souhaits = {};
  for (const item of desiderata || []) {
    if (!item || !item.nom || !item.prenom) continue;
    const key = `${item.nom}_${item.prenom}`;
    const valeurs = item.valeurs || {};

    souhaits[key] = {
      nom: item.nom,
      prenom: item.prenom,
      nbGardes: zeroGardesExcludes
        ? item.nbGardes == null || String(item.nbGardes).trim() === ""
          ? null
          : Number(item.nbGardes)
        : typeof item.nbGardes === "number"
          ? item.nbGardes
          : parseInt(item.nbGardes, 10) || 0,
      souhaits: Object.fromEntries(
        Object.entries(valeurs).map(([date, val]) => [
          date,
          typeof val === "object" && val !== null ? val.code : val,
        ])
      ),
    };
  }

  const dim = new Date(year, monthNum, 0).getDate();
  const jours = Array.from({ length: dim }, (_, i) => {
    return `${year}-${pad2(monthNum)}-${pad2(i + 1)}`;
  });

  const medics = Object.keys(souhaits);

  const getPref = (med, date) => souhaits[med]?.souhaits?.[date] ?? 0;

  // PR2 : un zéro déclaré exclut de la génération, sans effacer les disponibilités.
  const eligibles = medics.filter((med) => {
    if (zeroGardesExcludes && souhaits[med].nbGardes === 0) return false;
    const s = souhaits[med]?.souhaits || {};
    return Object.values(s).some((code) => code === 1 || code === 2);
  });

  return { souhaits, jours, medics, getPref, eligibles, zeroGardesExcludes };
}

// ═══════════════════════════════════════
// HELPER : conflit avec listes externes (PR1, Digestif)
// ═══════════════════════════════════════

/**
 * Construit des maps normalisées { date → medKey } pour les listes externes.
 * @param {Object|null} pr1Liste - { "YYYY-MM-DD": "NOM_PRENOM" }
 * @param {Object|null} digestifListe - idem
 * @param {Object|null} reanimationListe - idem (gardes)
 * @param {Object|null} thoraciqueListe - idem (astreintes)
 * @param {Object|null} orthopedieListe - idem (astreintes)
 * @returns {{ mapPR1: Object, mapDig: Object, mapDigPure: Object, mapRea: Object, mapTho: Object, mapOrt: Object }}
 */
function buildExternalMaps(pr1Liste, digestifListe, reanimationListe, thoraciqueListe, orthopedieListe) {
  function build(liste) {
    const m = {};
    if (!liste) return m;
    for (const [date, med] of Object.entries(liste)) {
      if (date.startsWith("_")) continue;
      if (med) m[date] = normalizeMedKey(med);
    }
    return m;
  }
  const mapPR1 = build(pr1Liste);
  const mapDigOnly = build(digestifListe);
  const mapRea = build(reanimationListe);
  // Gardes Réanimation = mêmes contraintes/pondération que les gardes PR1/Digestif.
  // On fusionne donc Digestif + Réanimation dans la map "gardes externes".
  const mapDig = { ...mapDigOnly, ...mapRea };
  const mapTho = build(thoraciqueListe);
  const mapOrt = build(orthopedieListe);
  return { mapPR1, mapDig, mapDigPure: mapDigOnly, mapRea, mapTho, mapOrt };
}

function buildPreviousExternalMaps(previousExternalLists) {
  return buildExternalMaps(
    previousExternalLists?.pr1 || null,
    previousExternalLists?.digestif || null,
    previousExternalLists?.reanimation || null,
    previousExternalLists?.thoracique || null,
    previousExternalLists?.orthopedie || null,
  );
}

/**
 * Compte le nombre de gardes/astreintes PR1, Digestif+Réanimation, Thoracique, Orthopédie
 * pour chaque médecin éligible PR2.
 * @param {Object} mapPR1 - map normalisée { date → medKeyNorm }
 * @param {Object} mapDig - map normalisée { date → medKeyNorm }
 * @param {Object} mapTho - map normalisée { date → medKeyNorm }
 * @param {Object} mapOrt - map normalisée { date → medKeyNorm }
 * @param {string[]} eligibles - clés médecins PR2
 * @returns {Object} { medKey: { gardesPR1, gardesDigestif, astreintesTho, astreintesOrt, totalExterne } }
 */
// Poids des astreintes dans le calcul du total :
// Gardes (PR1, Digestif, Réanimation) = 1, Astreintes (Thoracique, Orthopédie) = 0.5
const POIDS_GARDE = 1;
const POIDS_ASTREINTE = 0.5;

/**
 * Arrondi de la cible « points/médecin » au demi-point le plus proche
 * (ex. 4,4 ou 4,6 → 4,5 ; 4,1 → 4 ; 4,9 → 5).
 */
export function roundCibleDemiPoint(x) {
  if (!Number.isFinite(x)) return 0;
  return Math.round(x * 2) / 2;
}

/** Libellé stats : entiers sans décimale, demi-points avec une décimale. */
function formatCibleStats(cibleBrute) {
  const r = roundCibleDemiPoint(cibleBrute);
  return Number.isInteger(r) ? `${r}` : r.toFixed(1);
}

/** Écart minimal (indices de jours dans le mois) entre deux « activités » du même médecin : PR2, PR1, digestif, réanimation, astreintes Tho/Ort ; voir aussi `checkPrevWeekSpacingConflict` pour le mois précédent. */
export const MIN_ECART_GARDES_PR2 = 3;

/** Espacements testés pour la génération PR2 par engagement progressif. */
export const PR2_ENGAGEMENT_ECARTS_GARDE = [6, 5, 4, 3];

/** Une astreinte le J interdit une garde PR2 le J-1, J et J+1. */
export const PR2_ASTREINTE_ECART_FIXE = 2;

function countExternalGardes(mapPR1, mapDigPure, mapRea, mapTho, mapOrt, eligibles) {
  function countMap(map, weightFn) {
    const c = {};
    for (const [date, med] of Object.entries(map)) {
      const w = weightFn(date);
      c[med] = (c[med] || 0) + w;
    }
    return c;
  }
  const countPR1 = countMap(mapPR1, chargeGardeByDate);
  const countDig = countMap(mapDigPure, chargeGardeByDate);
  const countRea = countMap(mapRea, chargeGardeByDate);
  const countTho = countMap(mapTho, chargeAstreinteByDate);
  const countOrt = countMap(mapOrt, chargeAstreinteByDate);

  const result = {};
  for (const med of eligibles) {
    const medNorm = normalizeMedKey(med);
    const gPR1 = countPR1[medNorm] || 0;
    const gDig = countDig[medNorm] || 0;
    const gRea = countRea[medNorm] || 0;
    const gTho = countTho[medNorm] || 0;
    const gOrt = countOrt[medNorm] || 0;
    const totalExterne = gPR1 + gDig + gRea + gTho + gOrt;
    result[med] = {
      gardesPR1: gPR1,
      gardesDigestif: gDig,
      gardesReanimation: gRea,
      astreintesTho: gTho,
      astreintesOrt: gOrt,
      totalExterne
    };
  }
  return result;
}

/**
 * Espacement minimal (indices dans le mois) entre une PR2 candidate et toutes les activités du même médecin :
 * PR1, digestif, réanimation, astreintes tho/ort, et autres PR2 déjà posées.
 * PR1 / digestif / réanimation suivent la même règle (pas de fusion « métier » différente — seulement des cartes distinctes pour les messages).
 *
 * @param {string} med
 * @param {string} date - jour PR2 candidat "YYYY-MM-DD"
 * @param {Object} jourIndex - { dateStr -> index 0..n-1 } pour le mois courant
 * @param {Object} mapPR1 - map normalisée
 * @param {Object} mapDigPure - digestif seul
 * @param {Object} mapRea - réanimation seule
 * @param {Object} mapTho - astreinte thoracique
 * @param {Object} mapOrt - astreinte orthopédie
 * @param {Object} affectationsPR2 - { date -> medKey } (mois courant)
 * @param {number} minEcart - écart minimum entre indices (ex. 3 = au moins 3 jours « calendrier mois » entre deux activités)
 * @param {{ excludePr2Date?: string }} [opts] - ignorer ce jour dans les PR2 déjà posées (déplacement)
 * @returns {{ conflit: boolean, raison: string|null }}
 */
function checkPr2PlacementConflict(med, date, jourIndex, mapPR1, mapDigPure, mapRea, mapTho, mapOrt, affectationsPR2, minEcart, opts = {}) {
  const medNorm = normalizeMedKey(med);
  const idx = jourIndex[date];
  if (idx === undefined) return { conflit: false, raison: null };
  const excludePr2Date = opts.excludePr2Date;

  const labeledMaps = [
    [mapPR1 || {}, "PR1"],
    [mapDigPure || {}, "Digestif"],
    [mapRea || {}, "Réanimation"],
    [mapTho || {}, "Astreinte thoracique"],
    [mapOrt || {}, "Astreinte orthopédie"],
  ];
  for (const [map, label] of labeledMaps) {
    for (const [d, m] of Object.entries(map)) {
      if (d.startsWith("_") || !m) continue;
      if (normalizeMedKey(m) !== medNorm) continue;
      const di = jourIndex[d];
      if (di === undefined) continue;
      if (Math.abs(idx - di) < minEcart) {
        return { conflit: true, raison: `Espacement < ${minEcart}j avec ${label} (${d})` };
      }
    }
  }

  for (const [d, m] of Object.entries(affectationsPR2 || {})) {
    if (!d || d.startsWith("_")) continue;
    if (normalizeMedKey(m) !== medNorm) continue;
    if (excludePr2Date && d === excludePr2Date) continue;
    if (d === date) continue;
    const di = jourIndex[d];
    if (di === undefined) continue;
    if (Math.abs(idx - di) < minEcart) {
      return { conflit: true, raison: `Espacement < ${minEcart}j avec une autre PR2 (${d})` };
    }
  }

  return { conflit: false, raison: null };
}

function buildPrevWeekMap(prevWeekListe) {
  const m = {};
  if (!prevWeekListe) return m;
  for (const [date, med] of Object.entries(prevWeekListe)) {
    if (!date || date.startsWith("_")) continue;
    if (!med) continue;
    m[date] = normalizeMedKey(med);
  }
  return m;
}

function checkPrevWeekSpacingConflict(med, date, prevWeekMap, minEcart) {
  if (!prevWeekMap || Object.keys(prevWeekMap).length === 0) {
    return { conflit: false, raison: null };
  }
  const medNorm = normalizeMedKey(med);
  for (const [prevDate, prevMed] of Object.entries(prevWeekMap)) {
    if (prevMed !== medNorm) continue;
    const ecart = Math.abs(diffDaysIso(date, prevDate));
    if (ecart < minEcart) {
      return {
        conflit: true,
        raison: `Espacement PR2 mois précédent insuffisant (${prevDate}, écart ${ecart}j < ${minEcart}j)`,
      };
    }
  }
  return { conflit: false, raison: null };
}

/**
 * Contrôle d'espacement PR2 typé :
 * - autres gardes (PR1, Digestif, Réanimation, PR2) : écart dynamique de la passe courante
 * - astreintes (Thoracique, Orthopédie) : écart fixe J-1/J/J+1 interdit
 */
export function checkPr2TypedPlacementConflict(
  med,
  date,
  jourIndex,
  externalMaps,
  affectationsPR2,
  options = {},
) {
  const gardeMinEcart = Number.isFinite(options.gardeMinEcart)
    ? options.gardeMinEcart
    : PR2_ENGAGEMENT_ECARTS_GARDE[0];
  const astreinteMinEcart = Number.isFinite(options.astreinteMinEcart)
    ? options.astreinteMinEcart
    : PR2_ASTREINTE_ECART_FIXE;
  const excludePr2Date = options.excludePr2Date;
  const medNorm = normalizeMedKey(med);
  const idx = jourIndex[date];
  if (idx === undefined) return { conflit: false, raison: null, type: null };

  function distanceToCandidate(sourceDate) {
    const di = jourIndex[sourceDate];
    if (di !== undefined) return Math.abs(idx - di);
    if (/^\d{4}-\d{2}-\d{2}$/.test(sourceDate)) return Math.abs(diffDaysIso(date, sourceDate));
    return null;
  }

  const sources = [
    { type: "garde", label: "PR1", map: externalMaps?.mapPR1 || {} },
    { type: "garde", label: "Digestif", map: externalMaps?.mapDigPure || {} },
    { type: "garde", label: "Réanimation", map: externalMaps?.mapRea || {} },
    { type: "astreinte", label: "Astreinte thoracique", map: externalMaps?.mapTho || {} },
    { type: "astreinte", label: "Astreinte orthopédie", map: externalMaps?.mapOrt || {} },
    { type: "garde", label: "PR1 mois précédent", map: externalMaps?.previousExternalMaps?.mapPR1 || {} },
    { type: "garde", label: "Digestif mois précédent", map: externalMaps?.previousExternalMaps?.mapDigPure || {} },
    { type: "garde", label: "Réanimation mois précédent", map: externalMaps?.previousExternalMaps?.mapRea || {} },
    { type: "astreinte", label: "Astreinte thoracique mois précédent", map: externalMaps?.previousExternalMaps?.mapTho || {} },
    { type: "astreinte", label: "Astreinte orthopédie mois précédent", map: externalMaps?.previousExternalMaps?.mapOrt || {} },
  ];

  for (const source of sources) {
    const minEcart = source.type === "astreinte" ? astreinteMinEcart : gardeMinEcart;
    for (const [d, m] of Object.entries(source.map || {})) {
      if (!d || d.startsWith("_") || !m) continue;
      if (normalizeMedKey(m) !== medNorm) continue;
      const ecart = distanceToCandidate(d);
      if (ecart === null) continue;
      if (ecart < minEcart) {
        return {
          conflit: true,
          type: source.type,
          label: source.label,
          date: d,
          ecart,
          minEcart,
          raison: source.type === "astreinte"
            ? `${source.label} le ${d} : aucune PR2 possible le J-1/J/J+1`
            : `Espacement < ${minEcart}j avec ${source.label} (${d})`,
        };
      }
    }
  }

  for (const [d, m] of Object.entries(affectationsPR2 || {})) {
    if (!d || d.startsWith("_")) continue;
    if (excludePr2Date && d === excludePr2Date) continue;
    if (d === date) continue;
    if (normalizeMedKey(m) !== medNorm) continue;
    const di = jourIndex[d];
    if (di === undefined) continue;
    const ecart = Math.abs(idx - di);
    if (ecart < gardeMinEcart) {
      return {
        conflit: true,
        type: "garde",
        label: "PR2",
        date: d,
        ecart,
        minEcart: gardeMinEcart,
        raison: `Espacement < ${gardeMinEcart}j avec une autre PR2 (${d})`,
      };
    }
  }

  return { conflit: false, raison: null, type: null };
}

/** Équilibrage de charge : méthode indépendante des deux workflows historiques. */
export function generationPr2EquilibragePermanence(prepData, externalLists = {}, { responsibleExclusions = {} } = {}) {
  const ecartsGarde = [6, 5, 4, 3];
  const { souhaits, jours, getPref } = prepData;
  const affectations = {};
  const journal = [];
  const exclusions = [];
  const participants = Object.keys(souhaits).sort().filter((med) => {
    if (responsibleExclusions[med]?.excluded === true) {
      exclusions.push({ med, cause: "responsable", motif: String(responsibleExclusions[med].motif || "").trim(),
        raison: "Écarté de cette génération par décision du responsable." });
      return false;
    }
    const plafond = souhaits[med].nbGardes;
    if (!Number.isInteger(plafond) || plafond < 1) {
      exclusions.push({ med, raison: "Nombre de gardes nul ou invalide : médecin exclu de cette génération." });
      return false;
    }
    if (!jours.some((jour) => [1, 2].includes(getPref(med, jour)))) {
      exclusions.push({ med, raison: "Aucun jour déclaré disponible ce mois." });
      return false;
    }
    return true;
  });
  const jourIndex = Object.fromEntries(jours.map((d, i) => [d, i]));
  const maps = buildExternalMaps(externalLists.pr1Liste, externalLists.digestifListe,
    externalLists.reanimationListe, externalLists.thoraciqueListe, externalLists.orthopedieListe);
  maps.previousExternalMaps = buildPreviousExternalMaps(externalLists.previousExternalLists);
  const prevWeek = buildPrevWeekMap(externalLists.pr2PrevWeek);
  // Les bordures servent aux conflits seulement, jamais à la charge du mois.
  const inMonth = (map) => Object.fromEntries(Object.entries(map).filter(([d]) => jours.includes(d)));
  const gardesExternes = countExternalGardes(inMonth(maps.mapPR1), inMonth(maps.mapDigPure),
    inMonth(maps.mapRea), inMonth(maps.mapTho), inMonth(maps.mapOrt), participants);
  function decompteReel(med, guardMaps, onCallMaps) {
    const count = (sources) => {
      const counts = { semaine: 0, samedi: 0, dimancheFerie: 0 };
      const dates = new Set();
      for (const source of sources) for (const [date, assigned] of Object.entries(inMonth(source))) {
        if (normalizeMedKey(assigned) !== normalizeMedKey(med)) continue;
        if (dates.has(date)) continue;
        const coeff = coeffJour(date);
        counts[coeff === 2 ? "dimancheFerie" : coeff === 1.5 ? "samedi" : "semaine"] += 1;
        dates.add(date);
      }
      return counts;
    };
    const gardes = count(guardMaps), astreintes = count(onCallMaps);
    return { gardes, astreintes };
  }
  const nb = Object.fromEntries(participants.map((m) => [m, 0]));
  const points = { ...nb };
  const total = (med) => gardesExternes[med].totalExterne + points[med];
  const cible = participants.length
    ? (participants.reduce((s, m) => s + total(m), 0) + jours.reduce((s, d) => s + chargeGardeByDate(d), 0)) / participants.length
    : null;

  function obstacles(med, date, ecart) {
    const raisons = [];
    if (![1, 2].includes(getPref(med, date))) return ["Jour non déclaré disponible"];
    if (nb[med] >= souhaits[med].nbGardes) raisons.push("Plafond atteint");
    const typed = checkPr2TypedPlacementConflict(med, date, jourIndex, maps, affectations,
      { gardeMinEcart: ecart, astreinteMinEcart: PR2_ASTREINTE_ECART_FIXE });
    if (typed.conflit) raisons.push(typed.raison);
    const previous = checkPrevWeekSpacingConflict(med, date, prevWeek, ecart);
    if (previous.conflit) raisons.push(previous.raison);
    if (!pr2CalendrierAutoriseNouveauJour(date, med, affectations)) raisons.push("Quota samedi/dimanche/férié atteint");
    return raisons;
  }

  function pairs(ecart, aboveTarget) {
    const candidates = [];
    const demand = {};
    for (const med of participants) {
      const ownDates = Object.keys(affectations).filter((d) => affectations[d] === med);
      for (const date of jours) {
        if (affectations[date] || obstacles(med, date, ecart).length) continue;
        demand[date] = (demand[date] || 0) + 1;
        // Le seuil porte sur la charge AVANT attribution, sans limiter le franchissement.
        if (aboveTarget ? total(med) < cible : total(med) >= cible) continue;
        candidates.push({ med, date, pref: getPref(med, date),
          distance: ownDates.length ? Math.min(...ownDates.map((d) => Math.abs(diffDaysIso(date, d)))) : Infinity });
      }
    }
    const dayOrder = (a, b) => a.pref - b.pref ||
      (a.distance === b.distance ? 0 : b.distance - a.distance) ||
      demand[a.date] - demand[b.date] || a.date.localeCompare(b.date) || a.med.localeCompare(b.med);
    if (aboveTarget) {
      for (const c of candidates) {
        const loads = participants.map((m) => total(m) + (m === c.med ? chargeGardeByDate(c.date) : 0));
        c.max = Math.max(...loads);
        c.spread = c.max - Math.min(...loads);
      }
      candidates.sort((a, b) => a.max - b.max || a.spread - b.spread || dayOrder(a, b));
    } else candidates.sort((a, b) => total(a.med) - total(b.med) || dayOrder(a, b));
    return candidates;
  }

  function assign(candidate, ecart, etape) {
    const { med, date } = candidate;
    const avant = total(med);
    // Journaliser le relâchement réellement nécessaire, pas seulement le numéro de passe.
    const ecartNecessaire = ecartsGarde.find((gap) => obstacles(med, date, gap).length === 0);
    affectations[date] = med;
    nb[med] += 1;
    points[med] += chargeGardeByDate(date);
    journal.push({ med, date, etape, ecart, ecartNecessaire, avant, apres: total(med) });
  }
  for (const ecart of ecartsGarde) {
    let candidate;
    while ((candidate = pairs(ecart, false)[0])) assign(candidate, ecart, "sous_cible");
  }
  for (const ecart of [4, 3]) {
    let candidate;
    while ((candidate = pairs(ecart, true)[0])) assign(candidate, ecart, "au_dessus_cible");
  }

  const loads = participants.map(total);
  const moyenne = loads.length ? loads.reduce((s, n) => s + n, 0) / loads.length : null;
  const joursLibres = jours.filter((d) => !affectations[d]);
  const joursVides = joursLibres.map((date) => {
    const disponibles = participants.filter((m) => [1, 2].includes(getPref(m, date)));
    const details = disponibles.map((med) => ({ med, raisons: obstacles(med, date, 3) }));
    return { date, details, raisons: disponibles.length
      ? [...new Set(details.flatMap((d) => d.raisons))]
      : ["Aucun médecin participant disponible"] };
  });
  return {
    algorithme: "equilibrage_permanence", affectations, participants, exclusions, gardesExternes,
    cible, moyenne, maximum: loads.length ? Math.max(...loads) : null,
    minimum: loads.length ? Math.min(...loads) : null,
    ecartCharges: loads.length ? Math.max(...loads) - Math.min(...loads) : null,
    journal, relachements: journal.filter((j) => j.ecartNecessaire < 6), joursLibres, joursVides,
    joursAttribues: jours.filter((d) => affectations[d]),
    detailParMedecin: participants.map((med) => {
      const externe = decompteReel(med, [maps.mapPR1, maps.mapDigPure, maps.mapRea], [maps.mapTho, maps.mapOrt]);
      const pr2 = decompteReel(med, [affectations], []);
      const nuitsGarde = decompteReel(med, [maps.mapPR1, maps.mapDigPure, maps.mapRea, affectations], []).gardes;
      const nuitsAstreinte = externe.astreintes;
      return { med, nom: souhaits[med].nom, prenom: souhaits[med].prenom,
        plafond: souhaits[med].nbGardes, nbPr2: nb[med], externe: gardesExternes[med].totalExterne,
        pr2: points[med], total: total(med), ecartMoyenne: total(med) - moyenne,
        nuitsGarde, nuitsAstreinte,
        decompteExterne: { gardes: externe.gardes, astreintes: externe.astreintes },
        decomptePr2: { gardes: pr2.gardes, astreintes: pr2.astreintes } };
    }),
  };
}

/**
 * Génération PR2 "Engagement progressif" :
 * 1 garde chacun, puis 2e garde chacun, etc. ; chaque tour teste 6→5→4→3 jours
 * pour les gardes, tout en gardant l'écart astreinte fixe J-1/J/J+1.
 */
export function generationPr2EngagementProgressif(prepData, externalLists, options = {}) {
  const { souhaits, jours, getPref, eligibles } = prepData;
  const gardeEcarts = Array.isArray(options.gardeEcarts) && options.gardeEcarts.length > 0
    ? options.gardeEcarts
    : PR2_ENGAGEMENT_ECARTS_GARDE;
  const astreinteMinEcart = Number.isFinite(options.astreinteMinEcart)
    ? options.astreinteMinEcart
    : PR2_ASTREINTE_ECART_FIXE;
  const logs = [];
  const conflitsEvitesP2 = [];
  const passes = [];
  const arbitragesCollectifs = [];
  const affectations = {};
  const gardesPR2 = {};
  const gardesPR2Ponderees = {};
  const jourIndex = {};
  jours.forEach((d, i) => { jourIndex[d] = i; });

  const externalMaps = buildExternalMaps(
    externalLists?.pr1Liste || null,
    externalLists?.digestifListe || null,
    externalLists?.reanimationListe || null,
    externalLists?.thoraciqueListe || null,
    externalLists?.orthopedieListe || null,
  );
  externalMaps.previousExternalMaps = buildPreviousExternalMaps(externalLists?.previousExternalLists || null);
  const prevWeekMap = buildPrevWeekMap(externalLists?.pr2PrevWeek || null);
  const gardesExternes = countExternalGardes(
    externalMaps.mapPR1,
    externalMaps.mapDigPure,
    externalMaps.mapRea,
    externalMaps.mapTho,
    externalMaps.mapOrt,
    eligibles,
  );

  for (const med of eligibles) {
    gardesPR2[med] = 0;
    gardesPR2Ponderees[med] = 0;
  }

  const availableDaysByMed = {};
  const demandParJour = {};
  for (const jour of jours) {
    demandParJour[jour] = 0;
  }
  for (const med of eligibles) {
    const available = jours.filter((jour) => {
      const pref = getPref(med, jour);
      return pref === 1 || pref === 2;
    });
    availableDaysByMed[med] = available;
    for (const jour of available) demandParJour[jour] += 1;
  }

  const maxAvailable = Math.max(0, ...Object.values(availableDaysByMed).map((arr) => arr.length));

  logs.push("═══════════════════════════════════════════════════════════");
  logs.push("🚀 PR2 — ENGAGEMENT PROGRESSIF");
  logs.push(`👥 Éligibles : ${eligibles.length}`);
  logs.push(`📅 Jours dans le mois : ${jours.length}`);
  logs.push(`📏 Gardes : relaxation ${gardeEcarts.join("→")} j. | Astreintes : J-1/J/J+1 interdit`);
  logs.push("═══════════════════════════════════════════════════════════");

  function maxGardesDeclarees(med) {
    const declared = souhaits[med]?.nbGardes;
    const n = Number(declared);
    return declared != null && Number.isFinite(n) && (n > 0 || (prepData.zeroGardesExcludes && n === 0))
      ? n
      : Infinity;
  }

  function canTryEngagement(med, engagement) {
    return availableDaysByMed[med].length >= engagement && maxGardesDeclarees(med) >= engagement;
  }

  function maxEngagementForMed(med) {
    const declared = maxGardesDeclarees(med);
    return Math.min(availableDaysByMed[med].length, declared === Infinity ? availableDaysByMed[med].length : declared);
  }

  function placementOk(med, jour, gardeMinEcart) {
    const typed = checkPr2TypedPlacementConflict(
      med,
      jour,
      jourIndex,
      externalMaps,
      affectations,
      { gardeMinEcart, astreinteMinEcart },
    );
    if (typed.conflit) return typed;
    const prev = checkPrevWeekSpacingConflict(med, jour, prevWeekMap, gardeMinEcart);
    if (prev.conflit) {
      return { ...prev, type: "garde", label: "PR2 mois précédent" };
    }
    if (!pr2CalendrierAutoriseNouveauJour(jour, med, affectations)) {
      return {
        conflit: true,
        type: "calendrier",
        label: "Calendrier PR2",
        raison: "Déjà une PR2 samedi/dimanche/férié ce mois",
      };
    }
    return { conflit: false, raison: null };
  }

  function chooseBestDay(med, gardeMinEcart) {
    const candidats = [];
    for (const jour of availableDaysByMed[med]) {
      if (affectations[jour]) continue;
      const placement = placementOk(med, jour, gardeMinEcart);
      if (placement.conflit) {
        const [nom, prenom] = med.split("_");
        conflitsEvitesP2.push({ med, nom, prenom, date: jour, raison: placement.raison, ecart: gardeMinEcart, type: placement.type });
        continue;
      }
      const pref = getPref(med, jour);
      const datesMed = Object.entries(affectations)
        .filter(([, assigned]) => assigned === med)
        .map(([d]) => d);
      const minOwnGap = datesMed.length === 0
        ? Infinity
        : Math.min(...datesMed.map((d) => Math.abs(jourIndex[jour] - jourIndex[d])));
      candidats.push({ jour, pref, minOwnGap });
    }
    if (candidats.length === 0) return null;
    const medDejaWE = pr2MedecinAChargeWeekendOuFerie(med, affectations);
    candidats.sort((a, b) => {
      if (a.pref !== b.pref) return a.pref - b.pref;
      if (a.minOwnGap !== b.minOwnGap) return b.minOwnGap - a.minOwnGap;
      if (medDejaWE) {
        const ouvA = pr2JourEstSemaineOuvree(a.jour) ? 0 : 1;
        const ouvB = pr2JourEstSemaineOuvree(b.jour) ? 0 : 1;
        if (ouvA !== ouvB) return ouvA - ouvB;
      }
      const demandDiff = (demandParJour[a.jour] || 0) - (demandParJour[b.jour] || 0);
      if (demandDiff !== 0) return demandDiff;
      return a.jour.localeCompare(b.jour);
    });
    return candidats[0];
  }

  function computeConstraintProfile(med) {
    const byEcart = {};
    for (const gardeMinEcart of gardeEcarts) {
      let count = 0;
      for (const jour of availableDaysByMed[med]) {
        if (affectations[jour]) continue;
        if (!placementOk(med, jour, gardeMinEcart).conflit) count += 1;
      }
      byEcart[gardeMinEcart] = count;
    }
    return {
      byEcart,
      raw: availableDaysByMed[med].length,
      externalLoad: gardesExternes[med]?.totalExterne || 0,
    };
  }

  function compareByRealConstraints(a, b) {
    const profileA = computeConstraintProfile(a);
    const profileB = computeConstraintProfile(b);
    const ascendingEcarts = [...gardeEcarts].sort((x, y) => x - y);
    for (const ecart of ascendingEcarts) {
      const diff = (profileA.byEcart[ecart] || 0) - (profileB.byEcart[ecart] || 0);
      if (diff !== 0) return diff;
    }
    const raw = profileA.raw - profileB.raw;
    if (raw !== 0) return raw;
    const pr2 = gardesPR2[a] - gardesPR2[b];
    if (pr2 !== 0) return pr2;
    const ext = profileB.externalLoad - profileA.externalLoad;
    if (ext !== 0) return ext;
    return a.localeCompare(b);
  }

  function totalChargeCourante(med) {
    return (gardesExternes[med]?.totalExterne || 0) + (gardesPR2Ponderees[med] || 0);
  }

  function firstFeasibleEcart(med, jour) {
    for (const gardeMinEcart of gardeEcarts) {
      if (!placementOk(med, jour, gardeMinEcart).conflit) return gardeMinEcart;
    }
    return null;
  }

  function solveMinCostMatching(meds, dates, edgesByMed) {
    const source = 0;
    const medOffset = 1;
    const dateOffset = medOffset + meds.length;
    const sink = dateOffset + dates.length;
    const graph = Array.from({ length: sink + 1 }, () => []);

    function addEdge(from, to, cap, cost) {
      const forward = { to, rev: graph[to].length, cap, cost };
      const reverse = { to: from, rev: graph[from].length, cap: 0, cost: -cost };
      graph[from].push(forward);
      graph[to].push(reverse);
    }

    meds.forEach((med, i) => {
      addEdge(source, medOffset + i, 1, Math.round(totalChargeCourante(med) * 10000));
    });
    const dateIndex = Object.fromEntries(dates.map((date, i) => [date, i]));
    meds.forEach((med, i) => {
      for (const edge of edgesByMed[med] || []) {
        if (dateIndex[edge.jour] === undefined) continue;
        const relaxationPenalty = Math.max(0, gardeEcarts.indexOf(edge.ecart)) * 100;
        const prefPenalty = edge.pref === 1 ? 0 : 10;
        const demandPenalty = demandParJour[edge.jour] || 0;
        addEdge(medOffset + i, dateOffset + dateIndex[edge.jour], 1, relaxationPenalty + prefPenalty + demandPenalty);
      }
    });
    dates.forEach((_, i) => addEdge(dateOffset + i, sink, 1, 0));

    let flow = 0;
    let cost = 0;
    while (true) {
      const dist = Array(graph.length).fill(Infinity);
      const inQueue = Array(graph.length).fill(false);
      const prevNode = Array(graph.length).fill(-1);
      const prevEdge = Array(graph.length).fill(-1);
      const queue = [source];
      dist[source] = 0;
      inQueue[source] = true;
      while (queue.length > 0) {
        const node = queue.shift();
        inQueue[node] = false;
        graph[node].forEach((edge, edgeIndex) => {
          if (edge.cap <= 0) return;
          const nextDist = dist[node] + edge.cost;
          if (nextDist >= dist[edge.to]) return;
          dist[edge.to] = nextDist;
          prevNode[edge.to] = node;
          prevEdge[edge.to] = edgeIndex;
          if (!inQueue[edge.to]) {
            queue.push(edge.to);
            inQueue[edge.to] = true;
          }
        });
      }
      if (dist[sink] === Infinity) break;
      let aug = 1;
      for (let node = sink; node !== source; node = prevNode[node]) {
        if (node < 0) { aug = 0; break; }
        aug = Math.min(aug, graph[prevNode[node]][prevEdge[node]].cap);
      }
      if (aug <= 0) break;
      for (let node = sink; node !== source; node = prevNode[node]) {
        const edge = graph[prevNode[node]][prevEdge[node]];
        edge.cap -= aug;
        graph[edge.to][edge.rev].cap += aug;
      }
      flow += aug;
      cost += dist[sink] * aug;
    }

    const assignments = [];
    meds.forEach((med, i) => {
      const medNode = medOffset + i;
      for (const edge of graph[medNode]) {
        if (edge.to < dateOffset || edge.to >= sink || edge.cap !== 0) continue;
        const jour = dates[edge.to - dateOffset];
        const original = (edgesByMed[med] || []).find((candidate) => candidate.jour === jour);
        if (original) assignments.push({ med, jour, pref: original.pref, ecart: original.ecart });
      }
    });

    return { flow, cost, assignments };
  }

  function buildCollectiveComponents(pending) {
    const minEcart = Math.min(...gardeEcarts);
    const edgesByMed = {};
    const dateToMeds = {};
    for (const med of pending) {
      const edges = [];
      for (const jour of availableDaysByMed[med]) {
        if (affectations[jour]) continue;
        if (placementOk(med, jour, minEcart).conflit) continue;
        const ecart = firstFeasibleEcart(med, jour);
        if (!ecart) continue;
        edges.push({ jour, ecart, pref: getPref(med, jour) });
        if (!dateToMeds[jour]) dateToMeds[jour] = [];
        dateToMeds[jour].push(med);
      }
      edgesByMed[med] = edges;
    }

    const seenMeds = new Set();
    const components = [];
    for (const startMed of pending) {
      if (seenMeds.has(startMed) || (edgesByMed[startMed] || []).length === 0) continue;
      const meds = new Set();
      const dates = new Set();
      const queue = [{ type: "med", value: startMed }];
      seenMeds.add(startMed);
      while (queue.length > 0) {
        const item = queue.shift();
        if (item.type === "med") {
          meds.add(item.value);
          for (const edge of edgesByMed[item.value] || []) {
            if (dates.has(edge.jour)) continue;
            dates.add(edge.jour);
            queue.push({ type: "date", value: edge.jour });
          }
        } else {
          for (const med of dateToMeds[item.value] || []) {
            if (seenMeds.has(med)) continue;
            seenMeds.add(med);
            queue.push({ type: "med", value: med });
          }
        }
      }
      components.push({
        meds: [...meds].sort(compareByRealConstraints),
        dates: [...dates].sort(),
        edgesByMed,
      });
    }
    return components;
  }

  function applyCollectiveArbitration(pending, engagement, assignedByEcart) {
    const components = buildCollectiveComponents(pending);
    const blocked = [];
    for (const component of components) {
      const solution = solveMinCostMatching(component.meds, component.dates, component.edgesByMed);
      if (solution.flow < component.meds.length) {
        blocked.push({ ...component, solution });
      }
    }
    if (blocked.length === 0) return { applied: false, processedMeds: new Set() };

    const processedMeds = new Set();
    for (const component of blocked) {
      const assignedMeds = new Set(component.solution.assignments.map((item) => item.med));
      const nonAssigned = component.meds.filter((med) => !assignedMeds.has(med));
      const charges = component.meds
        .map((med) => ({ med, charge: totalChargeCourante(med), options: (component.edgesByMed[med] || []).map((edge) => edge.jour) }))
        .sort((a, b) => a.charge - b.charge || a.med.localeCompare(b.med));
      const arbitrage = {
        engagement,
        medecins: component.meds,
        dates: component.dates,
        flow: component.solution.flow,
        charges,
        assignments: component.solution.assignments,
        nonAssigned,
      };
      arbitragesCollectifs.push(arbitrage);

      logs.push(
        `   ⚖️ Arbitrage collectif détecté : ${component.meds.length} médecin(s), ${component.dates.length} créneau(x), ${component.solution.flow} attribution(s) possible(s)`
      );
      for (const item of charges) {
        const [nom, prenom] = item.med.split("_");
        logs.push(`     · charge ${formatCibleStats(item.charge)} — ${nom} ${prenom} — options: ${item.options.join(", ") || "aucune"}`);
      }

      for (const item of component.solution.assignments.sort((a, b) => a.jour.localeCompare(b.jour))) {
        if (affectations[item.jour] || gardesPR2[item.med] >= engagement) continue;
        const [nom, prenom] = item.med.split("_");
        affectations[item.jour] = item.med;
        gardesPR2[item.med] += 1;
        gardesPR2Ponderees[item.med] += chargeGardeByDate(item.jour);
        assignedByEcart[item.ecart] = (assignedByEcart[item.ecart] || 0) + 1;
        processedMeds.add(item.med);
        logs.push(
          `     ✅ arbitrage: ${nom} ${prenom} → ${item.jour} (${jourNomCourt(item.jour)}) [code ${item.pref}, écart gardes ${item.ecart}j, charge avant ${formatCibleStats(totalChargeCourante(item.med) - chargeGardeByDate(item.jour))}]`
        );
      }
      for (const med of nonAssigned) {
        const [nom, prenom] = med.split("_");
        processedMeds.add(med);
        logs.push(`     ⚠️ arbitrage: ${nom} ${prenom} non servi — charge avant ${formatCibleStats(totalChargeCourante(med))}`);
      }
    }
    return { applied: true, processedMeds };
  }

  function formatDateCourte(dateStr) {
    const dObj = parseIsoDate(dateStr);
    return `${dObj.getUTCDate()}/${dObj.getUTCMonth() + 1} (${jourNomCourt(dateStr)})`;
  }

  function buildEngagementRaisonNonServi(med) {
    const nbGardesDemandees = souhaits[med]?.nbGardes || 0;
    const ext = gardesExternes[med] || {
      gardesPR1: 0,
      gardesDigestif: 0,
      gardesReanimation: 0,
      astreintesTho: 0,
      astreintesOrt: 0,
      totalExterne: 0,
    };
    const joursDemandes = availableDaysByMed[med] || [];
    const joursDispoList = joursDemandes.map(formatDateCourte);

    if (nbGardesDemandees === 0 && joursDemandes.length === 0) {
      return { code: "PAS_DE_DESIDERATA", exclure: true };
    }

    if (joursDemandes.length === 0) {
      return {
        code: "AUCUNE_DISPONIBILITE",
        label: `A demandé ${nbGardesDemandees} garde(s) mais aucun jour disponible`,
        detail: `Aucune disponibilité exploitable (code 1 ou 2) sur les ${jours.length} jours du mois`,
        joursDispoList,
      };
    }

    const minEcart = Math.min(...gardeEcarts);
    const detailConflits = [];
    const gagnantMap = {};
    let nbBloqueExterne = 0;
    let nbPrisConcurrence = 0;
    let nbLibreNonRetenu = 0;

    for (const jour of joursDemandes) {
      const pref = getPref(med, jour);
      const prefTxt = pref === 1 ? "souhaite" : "disponible";
      const occupant = affectations[jour];
      const minPlacement = placementOk(med, jour, minEcart);
      const firstEcart = firstFeasibleEcart(med, jour);
      const parts = [];

      if (occupant && occupant !== med) {
        nbPrisConcurrence += 1;
        const [gNom, gPrenom] = occupant.split("_");
        const gExt = gardesExternes[occupant] || {
          gardesPR1: 0,
          gardesDigestif: 0,
          gardesReanimation: 0,
          astreintesTho: 0,
          astreintesOrt: 0,
          totalExterne: 0,
        };
        const gExtParts = [];
        if (gExt.gardesPR1 > 0) gExtParts.push(`${gExt.gardesPR1.toFixed(2)}PR1`);
        if (gExt.gardesDigestif > 0) gExtParts.push(`${gExt.gardesDigestif.toFixed(2)}Dig`);
        if (gExt.gardesReanimation > 0) gExtParts.push(`${gExt.gardesReanimation.toFixed(2)}Réa`);
        if (gExt.astreintesTho > 0) gExtParts.push(`${gExt.astreintesTho.toFixed(2)}Tho×0.5`);
        if (gExt.astreintesOrt > 0) gExtParts.push(`${gExt.astreintesOrt.toFixed(2)}Ort×0.5`);
        const totalGagnant = totalChargeCourante(occupant);
        const key = `${gNom}_${gPrenom}`;
        if (!gagnantMap[key]) {
          gagnantMap[key] = {
            nom: gNom,
            prenom: gPrenom,
            totalMin: totalGagnant,
            totalMax: totalGagnant,
            nb: 0,
            dates: [],
            extDetail: gExtParts.length > 0 ? gExtParts.join("+") : "0 ext.",
            pr2: gardesPR2[occupant] || 0,
          };
        }
        gagnantMap[key].nb += 1;
        gagnantMap[key].totalMin = Math.min(gagnantMap[key].totalMin, totalGagnant);
        gagnantMap[key].totalMax = Math.max(gagnantMap[key].totalMax, totalGagnant);
        gagnantMap[key].dates.push(formatDateCourte(jour));
        parts.push(`pris par ${gNom} ${gPrenom} (charge ${formatCibleStats(totalGagnant)})`);
      }

      if (minPlacement.conflit) {
        nbBloqueExterne += 1;
        parts.push(`bloqué même à ${minEcart}j : ${minPlacement.raison}`);
      } else if (!occupant) {
        nbLibreNonRetenu += 1;
        parts.push(firstEcart
          ? `exploitable à ${firstEcart}j mais non retenu par les tours d'engagement`
          : "aucun blocage détecté, mais non retenu");
      } else if (firstEcart) {
        parts.push(`aurait été exploitable à ${firstEcart}j`);
      }

      detailConflits.push(`${formatDateCourte(jour)} (${prefTxt}) : ${parts.join(" ; ") || "aucune cause identifiée"}`);
    }

    const arbitragesMed = arbitragesCollectifs.filter((a) => (a.nonAssigned || []).includes(med));
    const medExtParts = [];
    if (ext.gardesPR1 > 0) medExtParts.push(`${ext.gardesPR1.toFixed(2)}PR1`);
    if (ext.gardesDigestif > 0) medExtParts.push(`${ext.gardesDigestif.toFixed(2)}Dig`);
    if (ext.gardesReanimation > 0) medExtParts.push(`${ext.gardesReanimation.toFixed(2)}Réa`);
    if (ext.astreintesTho > 0) medExtParts.push(`${ext.astreintesTho.toFixed(2)}Tho×0.5`);
    if (ext.astreintesOrt > 0) medExtParts.push(`${ext.astreintesOrt.toFixed(2)}Ort×0.5`);
    const medExtLabel = medExtParts.length > 0 ? `${medExtParts.join(" + ")} = ${ext.totalExterne.toFixed(2)}` : "0";
    const parts = [];
    if (nbBloqueExterne > 0) parts.push(`${nbBloqueExterne} bloqué(s) par espacement/croisement`);
    if (nbPrisConcurrence > 0) parts.push(`${nbPrisConcurrence} pris par d'autres`);
    if (nbLibreNonRetenu > 0) parts.push(`${nbLibreNonRetenu} libre(s) non retenu(s)`);

    return {
      code: arbitragesMed.length > 0
        ? "BLOCAGE_COLLECTIF"
        : parts.length > 1
          ? "MIXTE"
          : nbPrisConcurrence > 0
            ? "CONCURRENCE"
            : "TOUS_BLOQUES",
      label: `${joursDemandes.length} jour(s) demandé(s) : ${parts.join(" + ") || "aucune attribution possible"}`,
      detail: `Charge externe pondérée : ${medExtLabel} — charge totale finale : ${totalChargeCourante(med).toFixed(2)}`,
      explication: arbitragesMed.length > 0
        ? "Blocage collectif détecté : les créneaux rares ne permettaient pas de servir tout le groupe, l'arbitrage a privilégié les charges pondérées les plus faibles."
        : "Analyse après attribution finale : chaque jour demandé était soit déjà pris, soit bloqué par les règles d'espacement/croisement.",
      detailConflits,
      joursDispoList,
      totalMed: ext.totalExterne,
      nbJoursDispo: joursDemandes.length,
      nbBloqueExterne,
      nbPrisConcurrence,
      nbLibreNonRetenu,
      arbitrages: arbitragesMed.map((a) => ({
        engagement: a.engagement,
        medecins: a.medecins,
        dates: a.dates,
        flow: a.flow,
        charges: a.charges,
        nonAssigned: a.nonAssigned,
      })),
      gagnants: Object.values(gagnantMap).sort((a, b) => a.totalMin - b.totalMin),
    };
  }

  for (let engagement = 1; engagement <= maxAvailable; engagement++) {
    if (Object.keys(affectations).length >= jours.length) break;
    const eligiblesEngagement = eligibles.filter((med) => canTryEngagement(med, engagement));
    if (eligiblesEngagement.length === 0) continue;

    logs.push("");
    logs.push(`── Tour ${engagement} : viser ${engagement} PR2 par médecin engagé (${eligiblesEngagement.length} candidat(s)) ──`);

    const passLog = {
      engagement,
      ecarts: gardeEcarts.map((ecart) => ({ ecart, assigned: 0 })),
    };
    const assignedByEcart = Object.fromEntries(gardeEcarts.map((ecart) => [ecart, 0]));
    const pending = eligiblesEngagement
      .filter((med) => gardesPR2[med] < engagement)
      .sort(compareByRealConstraints);

    const arbitration = applyCollectiveArbitration(pending, engagement, assignedByEcart);
    const pendingAfterArbitration = arbitration.applied
      ? pending.filter((med) => !arbitration.processedMeds.has(med) && gardesPR2[med] < engagement)
      : pending;

    logs.push(`   Ordre contraint réel : ${pending.length} médecin(s) restant(s)`);
    for (const med of pendingAfterArbitration) {
      if (Object.keys(affectations).length >= jours.length) break;
      if (gardesPR2[med] >= engagement) continue;
      const [nom, prenom] = med.split("_");
      let assignedMed = false;
      for (const gardeMinEcart of gardeEcarts) {
        const best = chooseBestDay(med, gardeMinEcart);
        if (!best) {
          logs.push(`     · ${nom} ${prenom} : rien à ${gardeMinEcart}j`);
          continue;
        }
        affectations[best.jour] = med;
        gardesPR2[med] += 1;
        gardesPR2Ponderees[med] += chargeGardeByDate(best.jour);
        assignedByEcart[gardeMinEcart] += 1;
        assignedMed = true;
        logs.push(`     ✅ ${nom} ${prenom} → ${best.jour} (${jourNomCourt(best.jour)}) [code ${best.pref}, écart gardes ${gardeMinEcart}j]`);
        break;
      }
      if (!assignedMed) logs.push(`     ⚠️ ${nom} ${prenom} : aucune date exploitable`);
    }
    passLog.ecarts = passLog.ecarts.map((entry) => ({ ...entry, assigned: assignedByEcart[entry.ecart] || 0 }));
    passes.push(passLog);
  }

  const joursAttribues = Object.keys(affectations).sort();
  const joursLibres = jours.filter((d) => !affectations[d]);
  const detailPhase2 = eligibles.map((med) => {
    const [nom, prenom] = med.split("_");
    const datesPR2 = Object.entries(affectations)
      .filter(([, assigned]) => assigned === med)
      .map(([d]) => d)
      .sort();
    const ext = gardesExternes[med] || { totalExterne: 0 };
    return {
      med,
      nom,
      prenom,
      nbGardesSouhaite: souhaits[med]?.nbGardes || 0,
      engagementMax: maxEngagementForMed(med),
      pr2Cible: maxEngagementForMed(med),
      garde1: datesPR2[0] || null,
      garde2: datesPR2[1] || null,
      gardesSupp: datesPR2.slice(2),
      datesPR2,
      totalPR2: datesPR2.length,
      totalPR2Pondere: gardesPR2Ponderees[med] || 0,
      totalGlobal: ext.totalExterne + (gardesPR2Ponderees[med] || 0),
      serviPhase2: datesPR2.length > 0,
      raisonNonServi: datesPR2.length === 0 ? buildEngagementRaisonNonServi(med) : null,
    };
  });
  const servisP2 = detailPhase2.filter((d) => d.serviPhase2);
  const nonServisP2 = detailPhase2.filter((d) => !d.serviPhase2);
  const totals = detailPhase2.map((d) => d.totalGlobal);
  const minTotal = totals.length > 0 ? Math.min(...totals) : 0;
  const maxTotal = totals.length > 0 ? Math.max(...totals) : 0;
  const avgTotal = totals.length > 0
    ? (totals.reduce((s, v) => s + v, 0) / totals.length).toFixed(1)
    : "0.0";

  logs.push("");
  logs.push("═══════════════════════════════════════════════════════════");
  logs.push("📊 RÉSUMÉ ENGAGEMENT PROGRESSIF");
  logs.push(`   Jours attribués : ${joursAttribues.length} / ${jours.length}`);
  logs.push(`   Jours restants  : ${joursLibres.length}`);
  logs.push(`   Médecins servis : ${servisP2.length} / ${eligibles.length}`);
  logs.push(`   Total gardes    : min=${minTotal}, max=${maxTotal}, moy=${avgTotal}`);
  logs.push("═══════════════════════════════════════════════════════════");

  return {
    affectations,
    joursAttribues,
    joursLibres,
    detailPhase2,
    servisP2,
    nonServisP2,
    conflitsEvitesP2,
    gardesExternes,
    gardesPR2,
    gardesPR2Ponderees,
    arbitragesCollectifs,
    pr2Cible: Object.fromEntries(eligibles.map((med) => [med, maxEngagementForMed(med)])),
    passes,
    ecartsGardes: gardeEcarts,
    astreinteMinEcart,
    stats: { minTotal, maxTotal, avgTotal, cible: "engagement" },
    algorithme: "engagement_progressif",
    logs,
  };
}

// ═══════════════════════════════════════
// PHASE 1 — 1 GARDE POUR CHACUN
// ═══════════════════════════════════════

/**
 * Phase 1 : attribuer exactement 1 garde à chaque médecin éligible.
 *
 * Stratégie :
 *  1. Pour chaque éligible, lister ses jours « code 1 » et ses jours « code 2 ».
 *  2. Trier les médecins par nombre de jours disponibles (code 1+2) croissant
 *     → les plus contraints passent en premier.
 *  3. Pour chaque médecin (dans cet ordre) :
 *     a. Parmi ses jours code 1 non encore pris → choisir celui le plus « libre »
 *        (le moins demandé par les autres médecins, puis le plus central dans le mois).
 *     b. Si aucun code 1 libre → idem avec ses jours code 2.
 *     c. Si rien de libre → le médecin est « non-servi ».
 *
 * @param {{ souhaits, jours, medics, getPref, eligibles }} prepData
 * @param {{ pr1Liste?: Object, digestifListe?: Object, thoraciqueListe?: Object, orthopedieListe?: Object }} [externalLists] - Listes externes pour filtrage anti-conflit
 * @returns {{ affectations, joursAttribues, servis, nonServis, detailParMedecin, logs, conflitsEvites }}
 */
export function phase1_uneGardeChacun(prepData, externalLists) {
  const { souhaits, jours, getPref, eligibles } = prepData;
  const logs = [];
  const conflitsEvites = []; // journal des exclusions

  // Listes externes (PR1, Digestif, Thoracique, Orthopédie) — optionnelles
  const { mapPR1, mapDig, mapDigPure, mapRea, mapTho, mapOrt } = buildExternalMaps(
    externalLists?.pr1Liste || null,
    externalLists?.digestifListe || null,
    externalLists?.reanimationListe || null,
    externalLists?.thoraciqueListe || null,
    externalLists?.orthopedieListe || null
  );
  const hasExternal = Object.keys(mapPR1).length > 0 || Object.keys(mapDig).length > 0 || Object.keys(mapTho).length > 0 || Object.keys(mapOrt).length > 0;

  // --- Compteur de gardes/astreintes externes par médecin ---
  const gardesExternes = countExternalGardes(mapPR1, mapDigPure, mapRea, mapTho, mapOrt, eligibles);

  logs.push("═══════════════════════════════════════════════════════════");
  logs.push("🚀 PHASE 1 — 1 garde pour chaque médecin éligible");
  logs.push(`👥 Éligibles : ${eligibles.length}`);
  logs.push(`📅 Jours dans le mois : ${jours.length}`);
  if (hasExternal) {
    logs.push(`🔀 Croisement actif : PR1 (${Object.keys(mapPR1).length} jours) / Digestif (${Object.keys(mapDig).length} jours)`);
    const withExt = eligibles.filter(m => gardesExternes[m].totalExterne > 0);
    logs.push(`📊 ${withExt.length} médecin(s) PR2 ont déjà des gardes PR1/Digestif ce mois`);
  }
  logs.push("═══════════════════════════════════════════════════════════");

  // --- Construire les listes de jours par médecin et par code ---
  const medecinJours = {}; // { medKey: { code1: [dates], code2: [dates] } }
  for (const med of eligibles) {
    const code1 = [];
    const code2 = [];
    for (const d of jours) {
      const p = getPref(med, d);
      if (p === 1) code1.push(d);
      else if (p === 2) code2.push(d);
    }
    medecinJours[med] = { code1, code2, total: code1.length + code2.length };
  }

  // --- Compter la « demande » par jour (combien de médecins le veulent en code 1 ou 2) ---
  const demandParJour = {};
  for (const d of jours) {
    let count = 0;
    for (const med of eligibles) {
      const p = getPref(med, d);
      if (p === 1 || p === 2) count++;
    }
    demandParJour[d] = count;
  }

  // --- Trier les médecins : moins de gardes externes d'abord, puis plus contraint ---
  const ordreAttribution = [...eligibles].sort((a, b) => {
    // 1. Moins de gardes externes (PR1+Digestif) → priorité (équité globale)
    const extDiff = gardesExternes[a].totalExterne - gardesExternes[b].totalExterne;
    if (extDiff !== 0) return extDiff;
    // 2. Plus contraint d'abord (moins de jours code 1+2)
    const diff = medecinJours[a].total - medecinJours[b].total;
    if (diff !== 0) return diff;
    // 3. À égalité, celui avec moins de code 1 passe en premier
    const diff1 = medecinJours[a].code1.length - medecinJours[b].code1.length;
    if (diff1 !== 0) return diff1;
    // 4. Alphabétique en dernier recours
    return a.localeCompare(b);
  });

  logs.push("");
  logs.push("📋 Ordre d'attribution (- gardes externes → + contraint → alpha) :");
  for (const med of ordreAttribution) {
    const mj = medecinJours[med];
    const ext = gardesExternes[med];
    const [nom, prenom] = med.split("_");
    const extLabel = ext.totalExterne > 0
      ? ` | ext: ${ext.gardesPR1} PR1 + ${ext.gardesDigestif} Dig = ${ext.totalExterne}`
      : " | ext: 0";
    logs.push(`   ${nom} ${prenom} — ${mj.code1.length} c1, ${mj.code2.length} c2 (${mj.total})${extLabel}`);
  }

  // --- Attribution ---
  const affectations = {}; // { date: medKey }
  const joursAttribues = new Set(); // dates déjà prises
  const detailParMedecin = []; // tableau pour l'affichage

  const jourIndex = {};
  jours.forEach((d, i) => { jourIndex[d] = i; });

  /**
   * Choisit le meilleur jour parmi une liste de candidats :
   *  - Pas déjà pris
   *  - Espacement minimal vs PR1, digestif, réa, astreintes et autres PR2 déjà posées
   *  - Demande la plus faible (jour le moins « disputé »)
   *  - En cas d'égalité, le jour le plus central dans le mois
   */
  const choisirMeilleurJour = (joursCandidats, med) => {
    const libres = joursCandidats.filter((d) => {
      if (joursAttribues.has(d)) return false;
      const { conflit, raison } = checkPr2PlacementConflict(
        med, d, jourIndex, mapPR1, mapDigPure, mapRea, mapTho, mapOrt, affectations, MIN_ECART_GARDES_PR2
      );
      if (conflit) {
        const [nom, prenom] = med.split("_");
        conflitsEvites.push({ med, nom, prenom, date: d, raison });
        return false;
      }
      return true;
    });
    if (libres.length === 0) return null;

    // Trier par : demande croissante, puis position centrale
    const milieu = jours.length / 2;
    libres.sort((a, b) => {
      const da = demandParJour[a] || 0;
      const db = demandParJour[b] || 0;
      if (da !== db) return da - db; // moins demandé en premier
      // Plus proche du milieu du mois
      const posA = Math.abs(jours.indexOf(a) - milieu);
      const posB = Math.abs(jours.indexOf(b) - milieu);
      return posA - posB;
    });

    return libres[0];
  };

  for (const med of ordreAttribution) {
    const mj = medecinJours[med];
    const [nom, prenom] = med.split("_");
    let jourChoisi = null;
    let codeChoisi = null;

    // Essayer d'abord avec code 1
    jourChoisi = choisirMeilleurJour(mj.code1, med);
    if (jourChoisi) {
      codeChoisi = 1;
    } else {
      // Sinon, essayer code 2
      jourChoisi = choisirMeilleurJour(mj.code2, med);
      if (jourChoisi) {
        codeChoisi = 2;
      }
    }

    if (jourChoisi) {
      affectations[jourChoisi] = med;
      joursAttribues.add(jourChoisi);
      logs.push(`  ✅ ${nom} ${prenom} → ${jourChoisi} (${jourNomCourt(jourChoisi)}) [code ${codeChoisi} — ${prefLabel(codeChoisi)}]`);
    } else {
      logs.push(`  ❌ ${nom} ${prenom} → aucun jour disponible`);
    }

    detailParMedecin.push({
      med,
      nom,
      prenom,
      nbGardesSouhaite: souhaits[med]?.nbGardes || 0,
      joursCode1: mj.code1.length,
      joursCode2: mj.code2.length,
      totalDispo: mj.total,
      jourAttribue: jourChoisi,
      codeAttribue: codeChoisi,
      servi: !!jourChoisi,
    });
  }

  // --- Résumé ---
  const servis = detailParMedecin.filter((d) => d.servi);
  const nonServis = detailParMedecin.filter((d) => !d.servi);
  const joursLibres = jours.filter((d) => !joursAttribues.has(d));

  logs.push("");
  logs.push("═══════════════════════════════════════════════════════════");
  logs.push("📊 RÉSUMÉ PHASE 1");
  logs.push(`   Médecins servis    : ${servis.length} / ${eligibles.length}`);
  logs.push(`   Médecins non-servis: ${nonServis.length}`);
  logs.push(`   Jours attribués    : ${joursAttribues.size} / ${jours.length}`);
  logs.push(`   Jours restants     : ${joursLibres.length}`);
  if (conflitsEvites.length > 0) {
    logs.push(`   🔀 Jours exclus (conflit PR1/Digestif) : ${conflitsEvites.length}`);
  }
  logs.push("═══════════════════════════════════════════════════════════");

  if (nonServis.length > 0) {
    logs.push("");
    logs.push("⚠️ Médecins non-servis :");
    for (const d of nonServis) {
      logs.push(`   - ${d.nom} ${d.prenom} (${d.joursCode1} code 1, ${d.joursCode2} code 2)`);
    }
  }

  if (conflitsEvites.length > 0) {
    logs.push("");
    logs.push("🔀 Jours exclus pour cause de conflit PR1/Digestif :");
    for (const c of conflitsEvites) {
      logs.push(`   - ${c.nom} ${c.prenom} : ${c.date} exclu → ${c.raison}`);
    }
  }

  return {
    affectations,
    joursAttribues: Array.from(joursAttribues),
    joursLibres,
    servis,
    nonServis,
    detailParMedecin,
    conflitsEvites,
    gardesExternes,
    logs,
  };
}

// ═══════════════════════════════════════
// PHASE 2 — 2e GARDE POUR CHACUN
// ═══════════════════════════════════════

/**
 * Phase 2 : attribuer une 2e garde à chaque médecin éligible déjà servi en Phase 1.
 *
 * Stratégie :
 *  1. Pour chaque médecin servi (1 garde), lister ses jours code 1 libres,
 *     puis code 2 libres (non pris, pas adjacent à sa 1re garde).
 *  2. Trier les médecins par nombre de jours code 1 libres croissant
 *     → les plus contraints passent en premier.
 *  3. Pour chaque médecin (dans cet ordre) :
 *     a. Parmi ses jours code 1 libres → choisir le plus éloigné de sa 1re garde.
 *     b. Si aucun code 1 libre → idem avec ses jours code 2 libres.
 *     c. Si rien → le médecin reste à 1 garde.
 *
 * @param {{ souhaits, jours, medics, getPref, eligibles }} prepData
 * @param {{ affectations, detailParMedecin }} phase1Result
 * @param {{ pr1Liste?: Object, digestifListe?: Object, thoraciqueListe?: Object, orthopedieListe?: Object }} [externalLists] - Listes externes pour filtrage anti-conflit
 * @returns {{ affectations, joursAttribues, detailPhase2, joursLibres, logs, conflitsEvitesP2 }}
 */
export function phase2_deuxiemeGarde(prepData, phase1Result, externalLists) {
  const { souhaits, jours, getPref, eligibles } = prepData;
  const logs = [];
  const conflitsEvitesP2 = [];

  // Listes externes (PR1, Digestif, Thoracique, Orthopédie) — optionnelles
  const { mapPR1, mapDig, mapDigPure, mapRea, mapTho, mapOrt } = buildExternalMaps(
    externalLists?.pr1Liste || null,
    externalLists?.digestifListe || null,
    externalLists?.reanimationListe || null,
    externalLists?.thoraciqueListe || null,
    externalLists?.orthopedieListe || null
  );
  const hasExternal = Object.keys(mapPR1).length > 0 || Object.keys(mapDig).length > 0 || Object.keys(mapTho).length > 0 || Object.keys(mapOrt).length > 0;

  // --- Compteur de gardes externes (PR1 + Digestif) par médecin ---
  const gardesExternes = countExternalGardes(mapPR1, mapDigPure, mapRea, {}, {}, eligibles);

  // Copier les affectations de Phase 1
  const affectations = { ...phase1Result.affectations };
  const joursAttribues = new Set(phase1Result.joursAttribues);

  // Index des jours pour calculer les distances
  const jourIndex = {};
  jours.forEach((d, i) => { jourIndex[d] = i; });

  logs.push("═══════════════════════════════════════════════════════════");
  logs.push("🚀 PHASE 2 — 2e garde pour chaque médecin");
  logs.push(`👥 Médecins servis en Phase 1 : ${phase1Result.servis.length}`);
  logs.push(`📅 Jours libres : ${jours.length - joursAttribues.size}`);
  if (hasExternal) {
    logs.push(`🔀 Croisement actif : PR1 (${Object.keys(mapPR1).length} jours) / Digestif (${Object.keys(mapDig).length} jours)`);
  }
  logs.push("═══════════════════════════════════════════════════════════");

  // Construire pour chaque médecin servi : sa 1re garde + jours code 1/2 libres
  const medecinsPhase2 = [];
  for (const d of phase1Result.servis) {
    const garde1 = d.jourAttribue;
    const garde1Index = jourIndex[garde1];

    // Jours code 1 libres, à au moins MIN_ECART_GARDES_PR2 jours de la 1re garde, sans conflit externe
    const code1Libres = [];
    const code2Libres = [];
    for (const jour of jours) {
      if (joursAttribues.has(jour)) continue; // déjà pris

      // Vérifier aussi que le nbGardes autorise une 2e garde
      const maxGardes = souhaits[d.med]?.nbGardes || 0;
      if (maxGardes > 0 && maxGardes < 2) continue; // le médecin ne veut qu'1 garde

      const { conflit, raison } = checkPr2PlacementConflict(
        d.med, jour, jourIndex, mapPR1, mapDigPure, mapRea, mapTho, mapOrt, affectations, MIN_ECART_GARDES_PR2
      );
      if (conflit) {
        conflitsEvitesP2.push({ med: d.med, nom: d.nom, prenom: d.prenom, date: jour, raison });
        continue;
      }

      const pref = getPref(d.med, jour);
      if (!pr2CalendrierAutoriseNouveauJour(jour, d.med, affectations)) continue;

      if (pref === 1) code1Libres.push(jour);
      else if (pref === 2) code2Libres.push(jour);
    }

    medecinsPhase2.push({
      ...d,
      garde1,
      garde1Index,
      code1Libres,
      code2Libres,
      totalLibres: code1Libres.length + code2Libres.length,
    });
  }

  // Trier : moins de gardes externes d'abord, puis plus contraint
  medecinsPhase2.sort((a, b) => {
    // 1. Moins de gardes externes → priorité
    const extDiff = (gardesExternes[a.med]?.totalExterne || 0) - (gardesExternes[b.med]?.totalExterne || 0);
    if (extDiff !== 0) return extDiff;
    // 2. Plus contraint d'abord
    const diff = a.totalLibres - b.totalLibres;
    if (diff !== 0) return diff;
    const diff1 = a.code1Libres.length - b.code1Libres.length;
    if (diff1 !== 0) return diff1;
    return a.med.localeCompare(b.med);
  });

  logs.push("");
  logs.push("📋 Ordre d'attribution Phase 2 (- gardes ext. → + contraint → alpha) :");
  for (const m of medecinsPhase2) {
    const ext = gardesExternes[m.med] || { gardesPR1: 0, gardesDigestif: 0, totalExterne: 0 };
    const extLabel = ext.totalExterne > 0
      ? ` | ext: ${ext.gardesPR1} PR1 + ${ext.gardesDigestif} Dig = ${ext.totalExterne}`
      : " | ext: 0";
    logs.push(`   ${m.nom} ${m.prenom} — garde 1 : ${m.garde1} (${jourNomCourt(m.garde1)}) — ${m.code1Libres.length} c1, ${m.code2Libres.length} c2${extLabel}`);
  }

  /**
   * Parmi une liste de jours, choisir le plus éloigné de garde1Index
   * et qui n'est pas encore pris.
   */
  const choisirPlusEloigne = (joursCandidats, garde1Idx, medKey) => {
    const libres = joursCandidats.filter((d) => !joursAttribues.has(d));
    if (libres.length === 0) return null;

    // Trier par distance décroissante depuis la 1re garde
    libres.sort((a, b) => {
      const distA = Math.abs(jourIndex[a] - garde1Idx);
      const distB = Math.abs(jourIndex[b] - garde1Idx);
      if (distA !== distB) return distB - distA; // plus éloigné d'abord
      if (medKey && pr2MedecinAChargeWeekendOuFerie(medKey, affectations)) {
        const ouvA = pr2JourEstSemaineOuvree(a) ? 0 : 1;
        const ouvB = pr2JourEstSemaineOuvree(b) ? 0 : 1;
        if (ouvA !== ouvB) return ouvA - ouvB;
      }
      return 0;
    });

    return libres[0];
  };

  // Attribution
  const detailPhase2 = [];
  for (const m of medecinsPhase2) {
    let jourChoisi = null;
    let codeChoisi = null;

    // Essayer code 1 d'abord
    jourChoisi = choisirPlusEloigne(m.code1Libres, m.garde1Index, m.med);
    if (jourChoisi) {
      codeChoisi = 1;
    } else {
      // Sinon code 2
      jourChoisi = choisirPlusEloigne(m.code2Libres, m.garde1Index, m.med);
      if (jourChoisi) {
        codeChoisi = 2;
      }
    }

    if (jourChoisi) {
      affectations[jourChoisi] = m.med;
      joursAttribues.add(jourChoisi);
      const distance = Math.abs(jourIndex[jourChoisi] - m.garde1Index);
      logs.push(`  ✅ ${m.nom} ${m.prenom} → ${jourChoisi} (${jourNomCourt(jourChoisi)}) [code ${codeChoisi}] — distance ${distance} jour(s) de la 1re garde`);
    } else {
      logs.push(`  ⚠️ ${m.nom} ${m.prenom} → aucun jour code 1 ou 2 libre (reste à 1 garde)`);
    }

    detailPhase2.push({
      med: m.med,
      nom: m.nom,
      prenom: m.prenom,
      nbGardesSouhaite: m.nbGardesSouhaite,
      garde1: m.garde1,
      garde2: jourChoisi,
      codeGarde2: codeChoisi,
      distance: jourChoisi ? Math.abs(jourIndex[jourChoisi] - m.garde1Index) : null,
      serviPhase2: !!jourChoisi,
    });
  }

  // Résumé
  const servisP2 = detailPhase2.filter((d) => d.serviPhase2);
  const nonServisP2 = detailPhase2.filter((d) => !d.serviPhase2);
  const joursLibres = jours.filter((d) => !joursAttribues.has(d));

  logs.push("");
  logs.push("═══════════════════════════════════════════════════════════");
  logs.push("📊 RÉSUMÉ PHASE 2");
  logs.push(`   Médecins avec 2e garde : ${servisP2.length} / ${medecinsPhase2.length}`);
  logs.push(`   Restés à 1 garde       : ${nonServisP2.length}`);
  logs.push(`   Jours attribués (total) : ${joursAttribues.size} / ${jours.length}`);
  logs.push(`   Jours restants          : ${joursLibres.length}`);
  if (conflitsEvitesP2.length > 0) {
    logs.push(`   🔀 Jours exclus (conflit PR1/Digestif) : ${conflitsEvitesP2.length}`);
  }
  logs.push("═══════════════════════════════════════════════════════════");

  if (conflitsEvitesP2.length > 0) {
    logs.push("");
    logs.push("🔀 Jours exclus en Phase 2 pour cause de conflit PR1/Digestif :");
    for (const c of conflitsEvitesP2) {
      logs.push(`   - ${c.nom} ${c.prenom} : ${c.date} exclu → ${c.raison}`);
    }
  }

  return {
    affectations,
    joursAttribues: Array.from(joursAttribues),
    joursLibres,
    detailPhase2,
    servisP2,
    nonServisP2,
    conflitsEvitesP2,
    gardesExternes,
    logs,
  };
}

// CROSS-CHECK : croiser PR2 avec les listes PR1 et Digestif
// ═══════════════════════════════════════════════════════════

/**
 * Normalise une clé médecin pour permettre le croisement entre systèmes.
 * Supprime les accents, met en majuscule, supprime les espaces superflus.
 * "NOM_Prenom" → "NOM_PRENOM"
 * "NOM Prenom" → "NOM_PRENOM"
 */
function normalizeMedKey(raw) {
  if (!raw) return "";
  return String(raw)
    .trim()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // supprime accents
    .toUpperCase()
    .replace(/\s+/g, "_");  // espaces → underscore
}

/** Déduit le tableau des jours du mois (YYYY-MM-DD) à partir des clés présentes dans les listes. */
function inferJoursMoisFromListes(...listes) {
  const allDates = [];
  for (const liste of listes) {
    if (!liste || typeof liste !== "object") continue;
    for (const k of Object.keys(liste)) {
      if (k.startsWith("_")) continue;
      if (/^\d{4}-\d{2}-\d{2}$/.test(k)) allDates.push(k);
    }
  }
  if (allDates.length === 0) return [];
  allDates.sort();
  const first = allDates[0];
  const [y, mo] = first.split("-").map(Number);
  const dim = new Date(y, mo, 0).getDate();
  const pad = (n) => String(n).padStart(2, "0");
  return Array.from({ length: dim }, (_, i) => `${y}-${pad(mo)}-${pad(i + 1)}`);
}

/**
 * Croisement PR2 vs listes externes — aligné sur `checkPr2PlacementConflict`
 * (écart minimal entre indices de jours dans le mois, par défaut MIN_ECART_GARDES_PR2).
 *
 * @param {Object} pr2Affectations    - { "YYYY-MM-DD": "NOM_PRENOM" }
 * @param {Object|null} pr1Liste
 * @param {Object|null} digestifListe
 * @param {Object|null} reanimationListe
 * @param {Object|null} thoraciqueListe
 * @param {Object|null} orthopedieListe
 * @param {{ jours?: string[], minEcart?: number }} [options] — `jours` = grille du mois (recommandé, même ordre que l’algo PR2)
 * @returns {Object} { conflits, alertes, resume }
 */
export function crossCheckGardes(
  pr2Affectations,
  pr1Liste,
  digestifListe,
  reanimationListe,
  thoraciqueListe,
  orthopedieListe,
  options = {}
) {
  const conflits = [];
  const alertes = [];

  const listesDisponibles = [];
  if (pr1Liste) listesDisponibles.push("PR1");
  if (digestifListe) listesDisponibles.push("Digestif");
  if (reanimationListe) listesDisponibles.push("Réanimation");
  if (thoraciqueListe) listesDisponibles.push("Thoracique");
  if (orthopedieListe) listesDisponibles.push("Orthopédie");

  if (listesDisponibles.length === 0) {
    alertes.push("Aucune liste externe disponible pour ce mois. Croisement impossible.");
    return { conflits, alertes, resume: { nbConflitsMemeJour: 0, nbConflitsConsecutifs: 0, listesDisponibles } };
  }

  const minEcart = options.minEcart ?? MIN_ECART_GARDES_PR2;
  let joursMois = options.jours;
  if (!joursMois || joursMois.length === 0) {
    joursMois = inferJoursMoisFromListes(
      pr2Affectations, pr1Liste, digestifListe, reanimationListe, thoraciqueListe, orthopedieListe
    );
  }
  const jourIndex = {};
  joursMois.forEach((d, i) => { jourIndex[d] = i; });

  const { mapPR1, mapDigPure, mapRea, mapTho, mapOrt } = buildExternalMaps(
    pr1Liste, digestifListe, reanimationListe, thoraciqueListe, orthopedieListe
  );

  const datesPR2 = Object.keys(pr2Affectations || {})
    .filter((k) => !k.startsWith("_") && /^\d{4}-\d{2}-\d{2}$/.test(k))
    .sort();

  for (const date of datesPR2) {
    const medRaw = pr2Affectations[date];
    if (!medRaw) continue;
    if (jourIndex[date] === undefined) continue;

    const { conflit, raison } = checkPr2PlacementConflict(
      medRaw, date, jourIndex, mapPR1, mapDigPure, mapRea, mapTho, mapOrt, pr2Affectations, minEcart
    );
    if (!conflit) continue;

    const medecin = normalizeMedKey(medRaw);
    let type = "consecutif";
    const m = raison && raison.match(/\((\d{4}-\d{2}-\d{2})\)\s*$/);
    if (m && m[1] === date) type = "meme_jour";

    conflits.push({
      date,
      medecin,
      listes: ["PR2"],
      type,
      detail: raison || "Espacement insuffisant",
    });
  }

  const nbConflitsMemeJour = conflits.filter((c) => c.type === "meme_jour").length;
  const nbConflitsConsecutifs = conflits.filter((c) => c.type === "consecutif").length;

  if (nbConflitsMemeJour > 0) {
    alertes.push(`⚠️ ${nbConflitsMemeJour} conflit(s) MÊME JOUR : PR2 et autre liste le même jour pour un même médecin.`);
  }
  if (nbConflitsConsecutifs > 0) {
    alertes.push(`⚠️ ${nbConflitsConsecutifs} conflit(s) d’ESPACEMENT : écart < ${minEcart} j (indices mois) entre PR2 et PR1 / digestif / réanimation / astreintes / autre PR2.`);
  }
  if (nbConflitsMemeJour === 0 && nbConflitsConsecutifs === 0) {
    alertes.push("✅ Aucun conflit détecté entre PR2 et les autres listes (PR1, Digestif, Réanimation, Thoracique, Orthopédie).");
  }

  return {
    conflits,
    alertes,
    resume: { nbConflitsMemeJour, nbConflitsConsecutifs, listesDisponibles },
  };
}

export function crossCheckGardesTyped(
  pr2Affectations,
  pr1Liste,
  digestifListe,
  reanimationListe,
  thoraciqueListe,
  orthopedieListe,
  options = {},
) {
  const conflits = [];
  const alertes = [];
  const listesDisponibles = [];
  if (pr1Liste) listesDisponibles.push("PR1");
  if (digestifListe) listesDisponibles.push("Digestif");
  if (reanimationListe) listesDisponibles.push("Réanimation");
  if (thoraciqueListe) listesDisponibles.push("Thoracique");
  if (orthopedieListe) listesDisponibles.push("Orthopédie");

  if (listesDisponibles.length === 0) {
    alertes.push("Aucune liste externe disponible pour ce mois. Croisement impossible.");
    return { conflits, alertes, resume: { nbConflitsMemeJour: 0, nbConflitsConsecutifs: 0, listesDisponibles } };
  }

  const gardeMinEcart = options.gardeMinEcart ?? PR2_ENGAGEMENT_ECARTS_GARDE[PR2_ENGAGEMENT_ECARTS_GARDE.length - 1];
  const astreinteMinEcart = options.astreinteMinEcart ?? PR2_ASTREINTE_ECART_FIXE;
  let joursMois = options.jours;
  if (!joursMois || joursMois.length === 0) {
    joursMois = inferJoursMoisFromListes(
      pr2Affectations,
      pr1Liste,
      digestifListe,
      reanimationListe,
      thoraciqueListe,
      orthopedieListe,
    );
  }
  const jourIndex = {};
  joursMois.forEach((d, i) => { jourIndex[d] = i; });

  const externalMaps = buildExternalMaps(
    pr1Liste,
    digestifListe,
    reanimationListe,
    thoraciqueListe,
    orthopedieListe,
  );
  externalMaps.previousExternalMaps = buildPreviousExternalMaps(options.previousExternalLists || null);

  const datesPR2 = Object.keys(pr2Affectations || {})
    .filter((k) => !k.startsWith("_") && /^\d{4}-\d{2}-\d{2}$/.test(k))
    .sort();

  for (const date of datesPR2) {
    const medRaw = pr2Affectations[date];
    if (!medRaw) continue;
    if (jourIndex[date] === undefined) continue;

    const { conflit, raison, type } = checkPr2TypedPlacementConflict(
      medRaw,
      date,
      jourIndex,
      externalMaps,
      pr2Affectations,
      { gardeMinEcart, astreinteMinEcart, excludePr2Date: date },
    );
    if (!conflit) continue;

    conflits.push({
      date,
      medecin: normalizeMedKey(medRaw),
      listes: ["PR2"],
      type: type === "astreinte" ? "astreinte_proche" : "consecutif",
      detail: raison || "Espacement insuffisant",
    });
  }

  const nbConflitsAstreinte = conflits.filter((c) => c.type === "astreinte_proche").length;
  const nbConflitsConsecutifs = conflits.filter((c) => c.type === "consecutif").length;
  if (nbConflitsConsecutifs > 0) {
    alertes.push(`⚠️ ${nbConflitsConsecutifs} conflit(s) d’ESPACEMENT : écart < ${gardeMinEcart} j entre PR2 et autre garde / PR2.`);
  }
  if (nbConflitsAstreinte > 0) {
    alertes.push(`⚠️ ${nbConflitsAstreinte} conflit(s) ASTREINTE : PR2 sur J-1/J/J+1 d'une astreinte.`);
  }
  if (conflits.length === 0) {
    alertes.push("✅ Aucun conflit détecté avec la règle typée : gardes ≥ 3 j, astreintes hors J-1/J/J+1.");
  }

  return {
    conflits,
    alertes,
    resume: {
      nbConflitsMemeJour: 0,
      nbConflitsConsecutifs,
      nbConflitsAstreinte,
      listesDisponibles,
    },
  };
}
